import React, { useCallback, useEffect, useRef, useState } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import { Spinner } from 'react-bootstrap';

// INDEXED DB
import { db, DB_THRESHOLD, getFilesTableSize } from 'db.js';

// LOCAL COMPONENTS
import Error from './Error';
import Viewer from './Viewer';

// STYLES
import styles from './viewFile.module.scss';

const ViewFile = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const tableSizeRef = useRef(0);
  let fileUrlRef = useRef();

  const handleError = useCallback(
    err => {
      // eslint-disable-next-line no-console
      if (err) console.error(`${err.message || 'Error'}:`, err);
      if (!error) {
        setError('File not found');
      }
    },
    [error]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fileName = params.get('fileName');

    const fetchData = async () => {
      try {
        setLoading(true);

        const fileData = await db.files.get(fileName);

        if (!fileData?.blob?.size) {
          throw new Error('File not found');
        }

        if (!file && fileData) {
          setFile(fileData);

          tableSizeRef.current = await getFilesTableSize();
          // eslint-disable-next-line no-console
          console.log(`File table size is ${(+tableSizeRef.current / 1024).toFixed(2)} KB`);
        }
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    const beforeUnloadHandler = async () => {
      if (fileUrlRef.current) window.URL.revokeObjectURL(fileUrlRef.current);

      if (fileName && tableSizeRef.current > DB_THRESHOLD) {
        await db.files.delete(fileName);
      }
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);

    return async () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, [file, fileUrlRef, handleError]);

  if (loading)
    return (
      <div className={clsx('d-flex flex-column align-items-center justify-content-center w-100', styles.preloader)}>
        <Spinner animation="border" role="status" variant="primary" className={styles.spinner} />
      </div>
    );

  if (error) {
    return <Error message={error || 'File not found'} />;
  }

  if (file) {
    const { fileName, blob, mimeType } = file || {};

    fileUrlRef.current = window.URL.createObjectURL(blob);

    return <Viewer fileName={fileName} fileUrl={fileUrlRef.current} mimeType={mimeType} onError={handleError} />;
  }

  return null;
};

export default ViewFile;
